import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { resolveMediaUrl } from '../utils/mediaUrl';
import { getTrackIdentity } from '../utils/profileEditorial';
import { hasExplicitSeekProgress } from '../utils/profileTrackPlayback';
import { registerUserScopedClientStateCleaner } from '../services/clientSessionLifecycle';

const TRACK_PLAY_REPORT_THRESHOLD_MS = 10 * 1000;
const AUDIO_PLAY_REPORT_START_METHOD = '__muzoneStartTrackPlayReport';
const AUDIO_PLAY_REPORT_STOP_METHOD = '__muzoneStopTrackPlayReport';

const noop = () => {};

const initialState = Object.freeze({
  activeSourceId: null,
  activeTrackId: null,
  activeTrack: null,
  activeSubjectId: null,
  sourceLabel: '',
  sourceSubtitle: '',
  sourceImage: null,
  isPlaying: false,
  isLoading: false,
  queue: [],
  queueIndex: -1,
  progressBySourceId: {},
});

const TrackPlaybackContext = createContext(null);

const getPlaybackClockTime = () => (
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()
);

export const buildTrackPlaybackSourceId = (sourceType = 'profile', subjectId = null) => {
  const normalizedSubjectId = subjectId == null ? '' : String(subjectId).trim();
  if (!normalizedSubjectId) {
    return null;
  }
  const normalizedSourceType = sourceType == null ? 'profile' : String(sourceType).trim().toLowerCase();
  return `${normalizedSourceType || 'profile'}:${normalizedSubjectId}`;
};

const normalizeQueue = (tracks) => (
  (Array.isArray(tracks) ? tracks : [])
    .filter((track) => Boolean(getTrackIdentity(track) && track?.objectKey))
);

const findQueueIndex = (queue, trackId) => (
  normalizeQueue(queue).findIndex((item) => getTrackIdentity(item) === trackId)
);

const resolveQueuedTrack = (queue, track) => {
  const trackId = getTrackIdentity(track);
  const queuedTrack = trackId
    ? normalizeQueue(queue).find((item) => getTrackIdentity(item) === trackId)
    : null;
  if (!queuedTrack) {
    return track;
  }
  return {
    ...queuedTrack,
    ...track,
    objectKey: track?.objectKey || queuedTrack.objectKey,
  };
};

const safeSetAudioAttribute = (audio, name, value = '') => {
  if (audio && typeof audio.setAttribute === 'function') {
    audio.setAttribute(name, value);
  }
};

export function TrackPlaybackProvider({ children, enabled = true }) {
  const [state, setState] = useState(initialState);
  const stateRef = useRef(initialState);
  const sourceRegistrationsRef = useRef(new Map());
  const activePlaybackRef = useRef(null);
  const audioPlayerRef = useRef(null);
  const activeAudioTrackIdRef = useRef(null);
  const activeAudioTrackUrlRef = useRef(null);
  const audioPlayRequestRef = useRef(0);
  const trackPlayReportQueueRef = useRef(Promise.resolve());
  const playTrackRef = useRef(null);
  const deferredProgressPruneTimerRef = useRef(null);
  const providerDisposedRef = useRef(false);

  const commitState = useCallback((updater) => {
    setState((current) => {
      const next = typeof updater === 'function' ? updater(current) : updater;
      stateRef.current = next;
      return next;
    });
  }, []);

  const getLatestSourceConfig = useCallback((sourceId) => {
    const registrations = sourceRegistrationsRef.current.get(sourceId);
    if (!registrations || registrations.size === 0) {
      return null;
    }
    const values = Array.from(registrations.values());
    return values[values.length - 1] || null;
  }, []);

  const pruneUnregisteredProgressFromState = useCallback((current, retainedSourceId = null) => {
    const progressBySourceId = current.progressBySourceId || {};
    let nextProgressBySourceId = progressBySourceId;

    Object.keys(progressBySourceId).forEach((sourceId) => {
      if (sourceId === retainedSourceId) {
        return;
      }
      const registrations = sourceRegistrationsRef.current.get(sourceId);
      if (registrations && registrations.size > 0) {
        return;
      }
      if (nextProgressBySourceId === progressBySourceId) {
        nextProgressBySourceId = { ...progressBySourceId };
      }
      delete nextProgressBySourceId[sourceId];
    });

    if (nextProgressBySourceId === progressBySourceId) {
      return current;
    }
    return {
      ...current,
      progressBySourceId: nextProgressBySourceId,
    };
  }, []);

  const pruneUnregisteredProgress = useCallback((retainedSourceId = null) => {
    commitState((current) => pruneUnregisteredProgressFromState(current, retainedSourceId));
  }, [commitState, pruneUnregisteredProgressFromState]);

  const clearDeferredProgressPrune = useCallback(() => {
    const timerId = deferredProgressPruneTimerRef.current;
    if (timerId === null) {
      return;
    }
    deferredProgressPruneTimerRef.current = null;
    if (typeof window !== 'undefined' && typeof window.clearTimeout === 'function') {
      window.clearTimeout(timerId);
      return;
    }
    clearTimeout(timerId);
  }, []);

  const scheduleUnregisteredProgressPrune = useCallback(() => {
    if (providerDisposedRef.current || deferredProgressPruneTimerRef.current !== null) {
      return;
    }
    const scheduleTimeout = typeof window !== 'undefined' && typeof window.setTimeout === 'function'
      ? window.setTimeout.bind(window)
      : setTimeout;
    deferredProgressPruneTimerRef.current = scheduleTimeout(() => {
      deferredProgressPruneTimerRef.current = null;
      if (providerDisposedRef.current) {
        return;
      }
      pruneUnregisteredProgress(activePlaybackRef.current?.sourceId || null);
    }, 0);
  }, [pruneUnregisteredProgress]);

  const syncActiveSourceConfig = useCallback((sourceId) => {
    const latestConfig = getLatestSourceConfig(sourceId);
    if (!latestConfig || activePlaybackRef.current?.sourceId !== sourceId) {
      return;
    }
    const activeTrackId = activePlaybackRef.current.activeTrackId;
    const nextQueue = normalizeQueue(latestConfig.queue);
    const nextQueueIndex = findQueueIndex(nextQueue, activeTrackId);
    const nextActiveTrack = resolveQueuedTrack(nextQueue, activePlaybackRef.current.activeTrack);
    activePlaybackRef.current = {
      ...activePlaybackRef.current,
      ...latestConfig,
      queue: nextQueue,
      queueIndex: nextQueueIndex,
      activeTrack: nextActiveTrack,
    };
    commitState((current) => {
      if (current.activeSourceId !== sourceId) {
        return current;
      }
      return {
        ...current,
        activeTrack: nextActiveTrack,
        sourceLabel: latestConfig.sourceLabel || current.sourceLabel,
        sourceSubtitle: latestConfig.sourceSubtitle || current.sourceSubtitle,
        sourceImage: latestConfig.sourceImage || current.sourceImage,
        queue: nextQueue,
        queueIndex: nextQueueIndex,
      };
    });
  }, [commitState, getLatestSourceConfig]);

  const registerSource = useCallback((config) => {
    if (!config?.sourceId || !config?.subjectId) {
      return noop;
    }
    const token = Symbol(config.sourceId);
    const registrations = sourceRegistrationsRef.current.get(config.sourceId) || new Map();
    registrations.set(token, {
      ...config,
      queue: normalizeQueue(config.queue),
    });
    sourceRegistrationsRef.current.set(config.sourceId, registrations);
    syncActiveSourceConfig(config.sourceId);
    return () => {
      const currentRegistrations = sourceRegistrationsRef.current.get(config.sourceId);
      if (!currentRegistrations) {
        return;
      }
      currentRegistrations.delete(token);
      if (currentRegistrations.size === 0) {
        sourceRegistrationsRef.current.delete(config.sourceId);
      }
      syncActiveSourceConfig(config.sourceId);
      scheduleUnregisteredProgressPrune();
    };
  }, [scheduleUnregisteredProgressPrune, syncActiveSourceConfig]);

  const updateTrackProgress = useCallback((sourceId, trackId, audio) => {
    if (!sourceId || !trackId || !audio) {
      return;
    }
    const duration = Number(audio.duration);
    const currentTime = Number(audio.currentTime);
    const progress = Number.isFinite(duration) && duration > 0
      ? Math.max(0, Math.min(1, currentTime / duration))
      : 0;
    commitState((current) => {
      const sourceProgress = current.progressBySourceId[sourceId] || {};
      if (sourceProgress[trackId] === progress) {
        return current;
      }
      return {
        ...current,
        progressBySourceId: {
          ...current.progressBySourceId,
          [sourceId]: {
            ...sourceProgress,
            [trackId]: progress,
          },
        },
      };
    });
  }, [commitState]);

  const reportTrackPlay = useCallback((playback, track, playSessionId) => {
    if (!playback?.subjectId || !track?.objectKey || !playSessionId) {
      return;
    }
    trackPlayReportQueueRef.current = trackPlayReportQueueRef.current
      .catch(() => null)
      .then(async () => {
        try {
          const response = await playback.recordTrackPlay(playback.subjectId, track.objectKey, playSessionId);
          const latestConfig = getLatestSourceConfig(playback.sourceId);
          if (typeof latestConfig?.onPlaysCountChange === 'function') {
            latestConfig.onPlaysCountChange(response?.playsCount, track, response);
          }
        } catch (error) {
          console.error(`Failed to record ${playback.logLabel} play:`, error);
        }
      });
  }, [getLatestSourceConfig]);

  const stopCurrentAudio = useCallback(({ cancelPending = true } = {}) => {
    if (cancelPending) {
      audioPlayRequestRef.current += 1;
    }
    const currentAudio = audioPlayerRef.current;
    if (!currentAudio) {
      return;
    }
    const sourceId = activePlaybackRef.current?.sourceId;
    const trackId = activeAudioTrackIdRef.current;
    if (typeof currentAudio[AUDIO_PLAY_REPORT_STOP_METHOD] === 'function') {
      currentAudio[AUDIO_PLAY_REPORT_STOP_METHOD]();
    }
    currentAudio.pause();
    updateTrackProgress(sourceId, trackId, currentAudio);
    audioPlayerRef.current = null;
    activeAudioTrackIdRef.current = null;
    activeAudioTrackUrlRef.current = null;
  }, [updateTrackProgress]);

  const pauseCurrentTrack = useCallback((track) => {
    audioPlayRequestRef.current += 1;
    const trackId = getTrackIdentity(track) || activeAudioTrackIdRef.current;
    const currentAudio = audioPlayerRef.current;
    if (currentAudio) {
      const sourceId = activePlaybackRef.current?.sourceId;
      if (typeof currentAudio[AUDIO_PLAY_REPORT_STOP_METHOD] === 'function') {
        currentAudio[AUDIO_PLAY_REPORT_STOP_METHOD]();
      }
      currentAudio.pause();
      updateTrackProgress(sourceId, trackId, currentAudio);
    }
    commitState((current) => ({
      ...current,
      activeTrackId: trackId || current.activeTrackId,
      isPlaying: false,
      isLoading: false,
    }));
  }, [commitState, updateTrackProgress]);

  const notifyPlaybackError = useCallback((playback, fallbackMessage) => {
    const latestConfig = getLatestSourceConfig(playback?.sourceId);
    const errorMessage = latestConfig?.errorMessage || playback?.errorMessage || fallbackMessage;
    if (typeof latestConfig?.onError === 'function') {
      latestConfig.onError(errorMessage);
    }
  }, [getLatestSourceConfig]);

  const startTrackPlayback = useCallback(async (sourceId, track, options = {}) => {
    if (!enabled || !sourceId || !track?.objectKey) {
      return;
    }
    const sourceConfig = getLatestSourceConfig(sourceId);
    const existingPlayback = activePlaybackRef.current?.sourceId === sourceId
      ? activePlaybackRef.current
      : null;
    const basePlayback = sourceConfig || existingPlayback;
    if (!basePlayback?.subjectId || typeof basePlayback.createTrackPlaySession !== 'function') {
      return;
    }

    const queue = normalizeQueue(options.queue || basePlayback.queue);
    const trackId = getTrackIdentity(track);
    if (!trackId) {
      return;
    }
    const resolvedTrack = resolveQueuedTrack(queue, track);
    const requestedSeek = Number(options.seekProgress);
    const shouldSeek = hasExplicitSeekProgress(options.seekProgress);
    const clampedSeek = shouldSeek ? Math.max(0, Math.min(1, requestedSeek)) : null;
    const currentAudio = audioPlayerRef.current;
    const isCurrentTrack = currentAudio
      && activePlaybackRef.current?.sourceId === sourceId
      && activeAudioTrackIdRef.current === trackId;

    if (isCurrentTrack && currentAudio.ended) {
      stopCurrentAudio({ cancelPending: false });
    } else if (isCurrentTrack) {
      activeAudioTrackIdRef.current = trackId;
      if (shouldSeek) {
        const duration = Number(currentAudio.duration);
        if (Number.isFinite(duration) && duration > 0) {
          currentAudio.currentTime = duration * clampedSeek;
          updateTrackProgress(sourceId, trackId, currentAudio);
        }
      }
      currentAudio.play().then(() => {
        if (typeof currentAudio[AUDIO_PLAY_REPORT_START_METHOD] === 'function') {
          currentAudio[AUDIO_PLAY_REPORT_START_METHOD]();
        }
        commitState((current) => ({
          ...current,
          isPlaying: true,
          isLoading: false,
          activeTrackId: trackId,
          activeTrack: resolvedTrack,
        }));
      }).catch((error) => {
        console.error(`Failed to resume ${basePlayback.logLabel}:`, error);
        commitState((current) => ({ ...current, isPlaying: false, isLoading: false }));
        notifyPlaybackError(basePlayback, basePlayback.errorMessage);
      });
      return;
    }

    stopCurrentAudio({ cancelPending: false });

    const nextQueueIndex = findQueueIndex(queue, trackId);
    const playback = {
      ...basePlayback,
      sourceId,
      queue,
      queueIndex: nextQueueIndex,
      activeTrackId: trackId,
      activeTrack: resolvedTrack,
      logLabel: basePlayback.logLabel || 'track',
    };
    activePlaybackRef.current = playback;
    commitState((current) => pruneUnregisteredProgressFromState({
      ...current,
      activeSourceId: sourceId,
      activeSubjectId: playback.subjectId,
      activeTrackId: trackId,
      activeTrack: resolvedTrack,
      sourceLabel: playback.sourceLabel || '',
      sourceSubtitle: playback.sourceSubtitle || '',
      sourceImage: playback.sourceImage || null,
      isPlaying: false,
      isLoading: true,
      queue,
      queueIndex: nextQueueIndex,
    }, sourceId));

    const playRequestId = audioPlayRequestRef.current + 1;
    audioPlayRequestRef.current = playRequestId;
    const isCurrentPlayRequest = () => audioPlayRequestRef.current === playRequestId;
    let playSession;
    try {
      playSession = await playback.createTrackPlaySession(playback.subjectId, resolvedTrack.objectKey);
    } catch (error) {
      if (!isCurrentPlayRequest()) {
        return;
      }
      console.error(`Failed to create ${playback.logLabel} play session:`, error);
      commitState((current) => ({ ...current, isPlaying: false, isLoading: false }));
      notifyPlaybackError(playback, playback.errorMessage);
      return;
    }
    if (!isCurrentPlayRequest() || audioPlayerRef.current) {
      return;
    }

    const streamUrl = playSession?.streamUrl ? resolveMediaUrl(playSession.streamUrl) : null;
    const playSessionId = playSession?.playSessionId;
    if (!streamUrl || !playSessionId || typeof document === 'undefined') {
      if (!isCurrentPlayRequest()) {
        return;
      }
      commitState((current) => ({ ...current, isPlaying: false, isLoading: false }));
      notifyPlaybackError(playback, playback.errorMessage);
      return;
    }

    const audio = document.createElement('audio');
    audio.crossOrigin = 'use-credentials';
    audio.preload = 'none';
    safeSetAudioAttribute(audio, 'controlsList', 'nodownload noremoteplayback');
    safeSetAudioAttribute(audio, 'disableRemotePlayback');
    audio.src = streamUrl;

    let playReportTimerId = null;
    let activePlaybackStartedAt = null;
    let accumulatedPlaybackMs = 0;
    let playReported = false;
    const clearPlayReportTimer = () => {
      if (playReportTimerId !== null && typeof window !== 'undefined') {
        window.clearTimeout(playReportTimerId);
        playReportTimerId = null;
      }
    };
    const getAccumulatedPlaybackMs = () => (
      activePlaybackStartedAt === null
        ? accumulatedPlaybackMs
        : accumulatedPlaybackMs + Math.max(0, getPlaybackClockTime() - activePlaybackStartedAt)
    );
    const reportPlayIfThresholdReached = () => {
      if (playReported || getAccumulatedPlaybackMs() < TRACK_PLAY_REPORT_THRESHOLD_MS) {
        return;
      }
      if (audioPlayerRef.current !== audio || activeAudioTrackIdRef.current !== trackId) {
        return;
      }
      playReported = true;
      clearPlayReportTimer();
      reportTrackPlay(playback, resolvedTrack, playSessionId);
    };
    const schedulePlayReportTimer = () => {
      clearPlayReportTimer();
      if (playReported || activePlaybackStartedAt === null || typeof window === 'undefined') {
        return;
      }
      const remainingMs = TRACK_PLAY_REPORT_THRESHOLD_MS - getAccumulatedPlaybackMs();
      if (remainingMs <= 0) {
        reportPlayIfThresholdReached();
        return;
      }
      playReportTimerId = window.setTimeout(reportPlayIfThresholdReached, remainingMs);
    };
    audio[AUDIO_PLAY_REPORT_START_METHOD] = () => {
      if (playReported || activePlaybackStartedAt !== null) {
        return;
      }
      activePlaybackStartedAt = getPlaybackClockTime();
      schedulePlayReportTimer();
    };
    audio[AUDIO_PLAY_REPORT_STOP_METHOD] = () => {
      if (activePlaybackStartedAt !== null) {
        accumulatedPlaybackMs += Math.max(0, getPlaybackClockTime() - activePlaybackStartedAt);
        activePlaybackStartedAt = null;
      }
      clearPlayReportTimer();
      reportPlayIfThresholdReached();
    };
    const applySeek = () => {
      if (!shouldSeek) {
        return;
      }
      const duration = Number(audio.duration);
      if (Number.isFinite(duration) && duration > 0) {
        audio.currentTime = duration * clampedSeek;
        updateTrackProgress(sourceId, trackId, audio);
      }
    };
    audio.addEventListener('loadedmetadata', applySeek, { once: true });
    audio.addEventListener('durationchange', () => updateTrackProgress(sourceId, trackId, audio));
    audio.addEventListener('timeupdate', () => updateTrackProgress(sourceId, trackId, audio));
    audio.addEventListener('pause', () => {
      if (typeof audio[AUDIO_PLAY_REPORT_STOP_METHOD] === 'function') {
        audio[AUDIO_PLAY_REPORT_STOP_METHOD]();
      }
      if (activeAudioTrackIdRef.current === trackId && !audio.ended) {
        commitState((current) => ({ ...current, isPlaying: false, isLoading: false }));
      }
    });
    audio.addEventListener('ended', () => {
      if (typeof audio[AUDIO_PLAY_REPORT_STOP_METHOD] === 'function') {
        audio[AUDIO_PLAY_REPORT_STOP_METHOD]();
      }
      if (activeAudioTrackIdRef.current !== trackId || activePlaybackRef.current?.sourceId !== sourceId) {
        return;
      }
      updateTrackProgress(sourceId, trackId, audio);
      const activePlayback = activePlaybackRef.current;
      const nextTrack = activePlayback.queue[activePlayback.queueIndex + 1];
      if (nextTrack) {
        playTrackRef.current?.(sourceId, nextTrack, { autoAdvance: true });
        return;
      }
      audioPlayerRef.current = audio;
      commitState((current) => ({
        ...current,
        isPlaying: false,
        isLoading: false,
        activeTrackId: trackId,
        activeTrack: resolvedTrack,
      }));
    });
    audio.onerror = () => {
      if (!isCurrentPlayRequest() || audioPlayerRef.current !== audio) {
        return;
      }
      if (typeof audio[AUDIO_PLAY_REPORT_STOP_METHOD] === 'function') {
        audio[AUDIO_PLAY_REPORT_STOP_METHOD]();
      }
      activeAudioTrackIdRef.current = null;
      activeAudioTrackUrlRef.current = null;
      audioPlayerRef.current = null;
      commitState((current) => ({
        ...current,
        isPlaying: false,
        isLoading: false,
      }));
      notifyPlaybackError(playback, playback.errorMessage);
    };
    audioPlayerRef.current = audio;
    activeAudioTrackIdRef.current = trackId;
    activeAudioTrackUrlRef.current = streamUrl;
    audio.play().then(() => {
      if (isCurrentPlayRequest() && audioPlayerRef.current === audio && activeAudioTrackIdRef.current === trackId) {
        commitState((current) => ({
          ...current,
          isPlaying: true,
          isLoading: false,
          activeTrackId: trackId,
          activeTrack: resolvedTrack,
        }));
        if (typeof audio[AUDIO_PLAY_REPORT_START_METHOD] === 'function') {
          audio[AUDIO_PLAY_REPORT_START_METHOD]();
        }
      }
    }).catch((error) => {
      if (!isCurrentPlayRequest()) {
        return;
      }
      console.error(`Failed to play ${playback.logLabel}:`, error);
      if (typeof audio[AUDIO_PLAY_REPORT_STOP_METHOD] === 'function') {
        audio[AUDIO_PLAY_REPORT_STOP_METHOD]();
      }
      if (audioPlayerRef.current === audio) {
        audioPlayerRef.current = null;
      }
      if (activeAudioTrackIdRef.current === trackId) {
        activeAudioTrackIdRef.current = null;
      }
      if (activeAudioTrackUrlRef.current === streamUrl) {
        activeAudioTrackUrlRef.current = null;
      }
      commitState((current) => ({ ...current, isPlaying: false, isLoading: false }));
      notifyPlaybackError(playback, playback.errorMessage);
    });
  }, [
    commitState,
    enabled,
    getLatestSourceConfig,
    notifyPlaybackError,
    pruneUnregisteredProgressFromState,
    reportTrackPlay,
    stopCurrentAudio,
    updateTrackProgress,
  ]);

  useEffect(() => {
    playTrackRef.current = startTrackPlayback;
  }, [startTrackPlayback]);

  const handleSeekTrack = useCallback((sourceId, track, progress) => {
    startTrackPlayback(sourceId, track, { seekProgress: progress });
  }, [startTrackPlayback]);

  const playNext = useCallback(() => {
    const playback = activePlaybackRef.current;
    if (!playback) {
      return;
    }
    const nextTrack = playback.queue[playback.queueIndex + 1];
    if (nextTrack) {
      startTrackPlayback(playback.sourceId, nextTrack);
    }
  }, [startTrackPlayback]);

  const playPrevious = useCallback(() => {
    const playback = activePlaybackRef.current;
    if (!playback) {
      return;
    }
    const previousTrack = playback.queue[playback.queueIndex - 1];
    if (previousTrack) {
      startTrackPlayback(playback.sourceId, previousTrack);
    }
  }, [startTrackPlayback]);

  const togglePlay = useCallback(() => {
    const playback = activePlaybackRef.current;
    if (!playback?.activeTrack) {
      return;
    }
    if (stateRef.current.isPlaying) {
      pauseCurrentTrack(playback.activeTrack);
      return;
    }
    startTrackPlayback(playback.sourceId, playback.activeTrack);
  }, [pauseCurrentTrack, startTrackPlayback]);

  const stopPlayback = useCallback(() => {
    clearDeferredProgressPrune();
    stopCurrentAudio({ cancelPending: true });
    activePlaybackRef.current = null;
    audioPlayerRef.current = null;
    activeAudioTrackIdRef.current = null;
    activeAudioTrackUrlRef.current = null;
    commitState(initialState);
  }, [clearDeferredProgressPrune, commitState, stopCurrentAudio]);

  useEffect(
    () => registerUserScopedClientStateCleaner(() => {
      stopPlayback();
      sourceRegistrationsRef.current.clear();
    }),
    [stopPlayback],
  );

  useEffect(() => {
    if (enabled) {
      return undefined;
    }
    stopPlayback();
    sourceRegistrationsRef.current.clear();
    return undefined;
  }, [enabled, stopPlayback]);

  useEffect(() => {
    providerDisposedRef.current = false;
    const sourceRegistrations = sourceRegistrationsRef.current;
    return () => {
      providerDisposedRef.current = true;
      stopPlayback();
      sourceRegistrations.clear();
    };
  }, [stopPlayback]);

  const value = useMemo(() => ({
    state,
    registerSource,
    playTrack: startTrackPlayback,
    seekTrack: handleSeekTrack,
    pauseCurrentTrack,
    togglePlay,
    playNext,
    playPrevious,
    stopPlayback,
  }), [
    handleSeekTrack,
    pauseCurrentTrack,
    playNext,
    playPrevious,
    registerSource,
    startTrackPlayback,
    state,
    stopPlayback,
    togglePlay,
  ]);

  return (
    <TrackPlaybackContext.Provider value={value}>
      {children}
    </TrackPlaybackContext.Provider>
  );
}

TrackPlaybackProvider.propTypes = {
  children: PropTypes.node.isRequired,
  enabled: PropTypes.bool,
};

export function useTrackPlayback() {
  const context = useContext(TrackPlaybackContext);
  if (!context) {
    throw new Error('useTrackPlayback must be used within TrackPlaybackProvider.');
  }
  return context;
}

export function useTrackPlaybackSource({
  sourceType = 'profile',
  subjectId,
  queue = [],
  createTrackPlaySession,
  recordTrackPlay,
  onPlaysCountChange,
  onError,
  errorMessage = 'Unable to play this track.',
  logLabel = 'track',
  sourceLabel = '',
  sourceSubtitle = '',
  sourceImage = null,
} = {}) {
  const {
    state,
    registerSource,
    playTrack,
    seekTrack,
    pauseCurrentTrack,
  } = useTrackPlayback();
  const sourceId = useMemo(
    () => buildTrackPlaybackSourceId(sourceType, subjectId),
    [sourceType, subjectId],
  );
  const sourceProgress = sourceId ? state.progressBySourceId[sourceId] || {} : {};
  const isActiveSource = state.activeSourceId === sourceId;

  const sourceConfig = useMemo(() => ({
    sourceId,
    sourceType,
    subjectId,
    queue,
    createTrackPlaySession,
    recordTrackPlay,
    onPlaysCountChange,
    onError,
    errorMessage,
    logLabel,
    sourceLabel,
    sourceSubtitle,
    sourceImage,
  }), [
    createTrackPlaySession,
    errorMessage,
    logLabel,
    onError,
    onPlaysCountChange,
    queue,
    recordTrackPlay,
    sourceId,
    sourceImage,
    sourceLabel,
    sourceSubtitle,
    sourceType,
    subjectId,
  ]);

  useEffect(() => (
    sourceId && subjectId
      ? registerSource(sourceConfig)
      : undefined
  ), [registerSource, sourceConfig, sourceId, subjectId]);

  const handlePlayTrack = useCallback((track, options = {}) => {
    if (!sourceId) {
      return;
    }
    const trackId = getTrackIdentity(track);
    if (options.pause === true && isActiveSource && state.activeTrackId === trackId) {
      pauseCurrentTrack(track);
      return;
    }
    playTrack(sourceId, track, options);
  }, [isActiveSource, pauseCurrentTrack, playTrack, sourceId, state.activeTrackId]);

  const handleSeekTrack = useCallback((track, progress) => {
    if (!sourceId) {
      return;
    }
    seekTrack(sourceId, track, progress);
  }, [seekTrack, sourceId]);

  return {
    playingTrackId: isActiveSource && state.isPlaying ? state.activeTrackId : null,
    trackProgressById: sourceProgress,
    handlePlayTrack,
    handleSeekTrack,
    pauseCurrentTrack,
  };
}
